/**
 * Shop Rating Component
 * Displays three types of ratings: VintMap Community, Owner, and Google
 * VintMap rating is clickable for authenticated users to add/edit their review
 */

import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Star, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { RatingModal } from "@/components/RatingModal";
import { LoginModal } from "@/components/LoginModal";
import { useAuth, getToken } from "@/lib/authContext";
import { useI18n } from "@/lib/i18n";
import { apiRequest } from "@/lib/queryClient";

interface ShopRatingProps {
  communityRating?: number | null; // VintMap Community rating
  communityReviewCount?: number | null; // VintMap review count (only show if > 0)
  ownerRating?: number | null; // Owner's own rating
  googleRating?: number | null; // Google rating
  googleReviewCount?: number | null; // Google review count
  googleReviewsUrl?: string | null; // Google reviews URL
  showLabels?: boolean; // Show rating type labels
  className?: string;
  shopId?: string; // Shop ID for rating modal
  shopName?: string; // Shop name for rating modal
  onRatingUpdate?: () => void; // Callback when rating is updated
  fetchUserReview?: boolean; // Whether to fetch the user's own review on mount (default: true)
}

/**
 * Render stars for a rating
 */
function renderStars(
  rating: number | string | null | undefined,
  maxStars: number = 5,
  className?: string
) {
  const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  if (numRating == null || isNaN(numRating)) {
    return Array(maxStars).fill(null).map((_, i) => (
      <Star
        key={i}
        className={cn("h-4 w-4 text-muted-foreground fill-muted-foreground/20", className)}
      />
    ));
  }

  const roundedRating = Math.round(numRating);
  return Array(maxStars).fill(null).map((_, i) => (
    <Star
      key={i}
      className={cn(
        "h-4 w-4",
        i < roundedRating
          ? "text-yellow-500 fill-yellow-500"
          : "text-muted-foreground fill-muted-foreground/20",
        className
      )}
    />
  ));
}

export function ShopRating({
  communityRating,
  communityReviewCount = 0,
  ownerRating,
  googleRating,
  googleReviewCount,
  googleReviewsUrl,
  showLabels = true,
  className = "",
  shopId,
  shopName,
  onRatingUpdate,
  fetchUserReview = true,
}: ShopRatingProps) {
  const { t } = useI18n();
  const { user, isLoggedIn } = useAuth();
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [existingReview, setExistingReview] = useState<any>(null);
  const [userReview, setUserReview] = useState<{
    averageRating: number;
    rating_atmosphere: number;
    rating_service: number;
    rating_price: number;
  } | null>(null);

  // Only show VintMap rating if there are actual VintMap reviews (not Google rating)
  const hasVintMapRating = (communityReviewCount ?? 0) > 0 && (communityRating != null && !isNaN(communityRating));

  const hasAnyRating =
    hasVintMapRating ||
    (ownerRating != null && !isNaN(ownerRating)) ||
    (googleRating != null && !isNaN(googleRating)) ||
    userReview !== null;

  // Load user's review on mount - ONLY if fetchUserReview is true
  useEffect(() => {
    if (user && shopId && fetchUserReview) {
      loadUserReview();
    } else {
      setUserReview(null);
      setExistingReview(null);
    }
  }, [user?.id, shopId, fetchUserReview]);

  async function loadUserReview() {
    if (!user || !shopId) return;

    try {
      const response = await apiRequest("GET", `/api/shops/${shopId}/reviews/my-review`);
      if (response.ok) {
        const data = await response.json();
        if (data.review) {
          setUserReview({
            averageRating: data.review.averageRating,
            rating_atmosphere: data.review.rating_atmosphere,
            rating_service: data.review.rating_service,
            rating_price: data.review.rating_price,
          });
          setExistingReview(data.review);
        } else {
          setUserReview(null);
          setExistingReview(null);
        }
      }
    } catch (error) {
      console.error("Error loading user review:", error);
      setUserReview(null);
      setExistingReview(null);
    }
  }

  // Load existing review when modal opens
  async function handleRatingClick() {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (!shopId) return;

    setRatingModalOpen(true);

    // Load user review if not already loaded
    if (!existingReview) {
      await loadUserReview();
    }
  }

  const isVintMapClickable = shopId && shopName;

  return (
    <>
      <div className={cn("space-y-2", className)}>
        {/* VintMap Community Rating - Clickable to rate */}
        <div
          className="flex flex-wrap sm:flex-nowrap items-center gap-2 min-w-0 cursor-pointer hover:opacity-80 transition-opacity group"
          onClick={(e) => {
            e.stopPropagation();
            handleRatingClick();
          }}
        >
          {showLabels && (
            <span className="text-xs font-medium text-muted-foreground w-14 sm:w-24 shrink-0 group-hover:text-primary transition-colors">
              {t("shopRating.vintMap")}
            </span>
          )}
          <div className="flex items-center gap-1 min-w-0">
            {renderStars(communityRating || 0)}
            <span className="text-xs text-muted-foreground ml-1 group-hover:text-primary transition-colors break-words">
              {hasVintMapRating
                ? `${Number(communityRating).toFixed(1)} (${communityReviewCount})`
                : t("shopRating.noReviews") || "geen reviews"}
            </span>
          </div>
        </div>

        {/* Shop Score (Owner Rating) - Shown if owner set a specific rating */}
        {ownerRating != null && ownerRating > 0 && (
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 min-w-0">
            {showLabels && (
              <span className="text-xs font-medium text-muted-foreground w-14 sm:w-24 shrink-0">
                {t("shopRating.ownerScore")}
              </span>
            )}
            <div className="flex items-center gap-1 min-w-0">
              {renderStars(ownerRating)}
              <span className="text-xs text-muted-foreground ml-1 break-words">
                {Number(ownerRating).toFixed(1)}
              </span>
            </div>
          </div>
        )}

        {/* Google Rating - Always visible */}
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 min-w-0">
          {showLabels && (
            <span className="text-xs font-medium text-muted-foreground w-14 sm:w-24 shrink-0">
              {t("shopRating.google")}
            </span>
          )}
          {googleReviewsUrl ? (
            <a
              href={googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 min-w-0 hover:opacity-80 transition-opacity group"
            >
              {renderStars(googleRating || 0)}
              <span className="text-xs text-muted-foreground ml-1 break-words">
                {googleRating != null ? Number(googleRating).toFixed(1) : "0.0"}
                {googleReviewCount != null && ` (${googleReviewCount})`}
              </span>
              <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          ) : (
            <div className="flex items-center gap-1 min-w-0">
              {renderStars(googleRating || 0)}
              <span className="text-xs text-muted-foreground ml-1 break-words">
                {googleRating != null ? Number(googleRating).toFixed(1) : "0.0"}
                {googleReviewCount != null && ` (${googleReviewCount})`}
              </span>
            </div>
          )}
        </div>

        {/* Jouw Score - User's own rating */}
        {userReview && (
          <Link href={`/shops/${shopId}#reviews`} className="flex flex-wrap sm:flex-nowrap items-center gap-2 pt-2 border-t border-border mt-2 min-w-0 cursor-pointer hover:opacity-80 transition-opacity">
            {showLabels && (
              <span className="text-xs font-medium text-muted-foreground w-14 sm:w-24 shrink-0">
                {t("shopRating.yourScore")}
              </span>
            )}
            <div className="flex items-center gap-1 min-w-0">
              {renderStars(userReview.averageRating)}
              <span className="text-xs text-muted-foreground ml-1 break-words">
                {userReview.averageRating.toFixed(1)}
              </span>
            </div>
          </Link>
        )}

        {/* Write/Edit Review Button */}
        {isVintMapClickable && (
          <div className="pt-2 mt-2 border-t border-border">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRatingClick();
              }}
              className="text-sm text-primary hover:underline font-medium flex items-center gap-1"
            >
              {userReview ? t("shopRating.editReview") : t("shopRating.writeReview")}
            </button>
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {shopId && shopName && (
        <RatingModal
          open={ratingModalOpen}
          onOpenChange={setRatingModalOpen}
          shopId={shopId}
          shopName={shopName}
          existingReview={existingReview}
          onSuccess={() => {
            loadUserReview(); // Reload user review after update
            onRatingUpdate?.();
            setRatingModalOpen(false);
          }}
        />
      )}
      {/* Login Modal */}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </>
  );
}